<template>
  <div class="p-4">
    <div class="grid grid-cols-5 items-center gap-5">
      <lf-dropdown
        :options="PERIOD_MONTHS"
        :value="chartInput.periodLength"
        @change="handlePeriodLengthChange"
        :name="$t('DEALS.CODAT.PROFIT_AND_LOSS.PERIOD_LENGTH')"
        :placeholder="$t('DEALS.CODAT.PROFIT_AND_LOSS.PERIOD_LENGTH')"
      />
      <do-month-picker
        :is-date-range="Number(chartInput.periodLength) > 1"
        only-date-picker
        :is-disabled="!chartInput.periodLength"
        @context:change="handleContextChange"
        :value="chartInput.month"
      />
      <lf-input
        :no-margin="true"
        @key-released="chartInput.periodsToCompare = $event"
        :value="chartInput.periodsToCompare"
        :name="$t('DEALS.CODAT.PROFIT_AND_LOSS.PERIODS_TO_COMPARE')"
        :placeholder="$t('DEALS.CODAT.PROFIT_AND_LOSS.PERIODS_TO_COMPARE')"
      />
      <div class="border-l-2 pl-3">
        <p class="text-xs text-label">
          {{ $t("DEALS.CODAT.PROFIT_AND_LOSS.CURRENCY") }}
        </p>
        <div
          v-if="profitAndLossBody?.currency"
          class="flex content-center items-center"
        >
          <icon-base
            :icon="CURRENCY_ICON[profitAndLossBody?.currency]"
            :width="28"
            :height="15"
            view-box="0 0 60 30"
            class="mr-4 place-self-center"
          />
          <div>
            <p class="text-sm font-normal text-headline">
              {{ profitAndLossBody?.currency || "-" }}
            </p>
          </div>
        </div>
      </div>
      <primary-button @click="onChangeParameters">
        {{ $t("DEALS.CODAT.PROFIT_AND_LOSS.REFRESH") }}
      </primary-button>
    </div>
    <div class="mt-7 grid grid-cols-4 items-center gap-5">
      <data-card
        v-for="(data, index) in profitLossData"
        :key="index"
        :show-progress-bar="false"
        :value="data.value"
      >
        <div class="flex items-center justify-between">
          <p>
            {{ data.label }}
          </p>
        </div>
      </data-card>
    </div>
    <div class="mt-7">
      <line-chart
        :legend="false"
        :tooltips="['Sentence 1', 'Sentence 2', 'Sentence 3']"
        :curve="false"
        thousands=","
        :data="chartData"
        :dataset="{ pointBackgroundColor: 'rgba(255, 255, 255, 1)' }"
      ></line-chart>
      <div class="mt-6 flex justify-center">
        <div
          v-for="(item, index) in legend"
          :key="index"
          class="flex items-center"
        >
          <div
            class="circle mx-2"
            :style="{ 'border-color': item.color }"
          ></div>
          <p class="text-xs uppercase">{{ item.label }}</p>
        </div>
      </div>
    </div>
    <div class="mt-7">
      <codat-table
        :reports-data="reports"
        :data-filter="dataFilter"
        :chart-filter="chartFilter"
        @update:data-values="handleDataValuesUpdate"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";

import { getObjectKeys, looksLikeNumber } from "@/helpers/common";
import { CURRENCY_ICON, PERIOD_MONTHS } from "@/helpers/constants";
import {
  CODAT_CHART_COLORS,
  PROFIT_AND_LOSS_FIXED_TEXT,
} from "@/helpers/constants";
import { formatMoney } from "@/helpers/formatting";

import CodatTable from "@/components/codat/CodatTable.vue";
import DataCard from "@/views/businessCreditDeals/components/dnb/DataCard.vue";

import type {
  ChartData,
  ChartDataInput,
  Codat,
  CodatLegend,
  RestructuredResult,
  SectionBody,
  SectionData,
} from "@/models/commercialData/Codat";
import type { LookbackValue } from "@/models/orchestration";
import type { PropType } from "vue";

type TextKey = keyof typeof PROFIT_AND_LOSS_FIXED_TEXT;

const props = defineProps({
  data: {
    type: Object as PropType<Codat>,
    default: null,
  },
  legend: {
    type: Object as PropType<CodatLegend[]>,
    required: true,
  },
  dataFilter: {
    type: Array as PropType<string[]>,
    required: true,
  },
  chartFilter: {
    type: Array as PropType<string[]>,
    required: true,
  },
});

const chartInput = ref<ChartDataInput>({
  month: [],
  periodLength: "",
  periodsToCompare: "",
});

const profitLossData = ref<SectionData[] | null>(null);
const chartData = ref<ChartData[] | null>(null);

const profitAndLossBody = computed<SectionBody>(
  () => props.data?.response_body[1] || []
);

const reports = ref(profitAndLossBody.value?.reports || []);

const getProfitData = (profitValues: RestructuredResult[]) => {
  const data = profitValues.filter(
    (el) => !el.fatherName && props.dataFilter.includes(el.id) && el.category
  );
  profitLossData.value = data
    .reduce<{ label: string; value: string }[][]>((acum, curr) => {
      const temp = {
        label: getPropertyText(curr.id),
        value: 0,
      };
      const keys = getObjectKeys(curr);

      keys.forEach((key) => {
        const date = new Date(key);
        if (date.toString() !== "Invalid Date") {
          temp.value += looksLikeNumber(curr[key]?.toString())
            ? Number(curr[key])
            : 0;
        }
      });
      const obj = [temp].map((el) => {
        return {
          label: el.label || "-",
          value: formatMoney(el.value),
        };
      });
      acum.push(obj);
      return acum;
    }, [])
    .flat();
};

const handleContextChange = (context: LookbackValue) => {
  chartInput.value.month = context.lookback.map((period) => String(period));
};

const handleDataValuesUpdate = (data: RestructuredResult[]) => {
  getChartValues(data);
  getProfitData(data);
};

const handlePeriodLengthChange = (period: string) => {
  chartInput.value.month = [];
  chartInput.value.periodLength = period;
};

const getChartValues = (chartValues: RestructuredResult[]) => {
  const data = chartValues.filter(
    (el) => !el.fatherName && props.chartFilter.includes(el.id) && el.category
  );
  chartData.value = data.map((el) => {
    const newObj: ChartData = {
      data: {},
      name: getPropertyText(el.id) || "-",
      color: CODAT_CHART_COLORS[el.id],
    };
    const filter = [
      "id",
      "hasChild",
      "open",
      "childName",
      "unit",
      "category",
      "fatherName",
    ];
    const keys = getObjectKeys(el);

    keys.forEach((key) => {
      if (!filter.includes(key)) {
        newObj.data[key] = looksLikeNumber(el[key]?.toString())
          ? Number(el[key])
          : 0;
      }
    });
    return newObj;
  });
};
const getPropertyText = (text: string) => {
  return PROFIT_AND_LOSS_FIXED_TEXT[text as TextKey]
    ? PROFIT_AND_LOSS_FIXED_TEXT[text as TextKey]
    : text;
};

const onChangeParameters = () => {
  const datesLength = chartInput.value.month.length;
  const fromDate = datesLength > 0 ? new Date(chartInput.value.month[0]) : null;
  const toDate = datesLength > 1 ? new Date(chartInput.value.month[1]) : null;
  reports.value =
    profitAndLossBody.value?.reports?.filter((report) => {
      const fromInitialDate = report.fromDate || report.date;
      const fromFinalDate = report.toDate || report.date;
      if (toDate && fromInitialDate && fromDate && fromFinalDate) {
        return (
          new Date(fromInitialDate).getTime() >= new Date(fromDate).getTime() &&
          new Date(fromFinalDate).getTime() <= new Date(toDate).getTime()
        );
      } else if (fromInitialDate && fromDate) {
        return (
          new Date(fromInitialDate).getTime() >= new Date(fromDate).getTime()
        );
      }
    }) || [];
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.circle {
  height: 15px;
  width: 15px;
  border: 3px solid black;
  border-radius: 50%;
}
</style>
